/**
 * @fileoverview Main Room Management page component with tabs and navigation
 * @author Hospital Room Scheduler Team
 * @created 2025-01-08
 * @updated 08-10-2025 02:36:57 - Added URL sync and RoomDetailSidebar integration
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Title,
  Tabs,
  Stack,
  Group,
  Button,
  Text,
  Badge,
  Paper,
  Alert,
  Loader,
  Center
} from '@mantine/core';
import { IconPlus, IconRefresh, IconAlertCircle } from '@tabler/icons-react';
import { useRooms, useRoomStats, useCreateRoom, useDeleteRoom } from '../../../hooks/admin/useRoomManagement';
import { useBuildings } from '../../../hooks/admin/useBuildings';
import { RoomForm } from '@/components/forms/RoomForm';
import { RoomList } from '@/components/organisms/RoomList';
import { RoomStats } from '@/components/organisms/RoomStats';
import { RoomDetailSidebar } from '../../../components/drawers';
import type { GetRoomsParams, CreateRoomData, UpdateRoomData } from '../../../api/endpoints/rooms';

/**
 * Main Room Management component
 * Provides comprehensive room management interface with tabs for different views
 */
export const RoomManagement: React.FC = () => {
  // =============================================================================
  // URL SYNC STATE MANAGEMENT
  // =============================================================================
  
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get roomId from URL
  const roomIdParam = searchParams.get('roomId');
  
  // =============================================================================
  // STATE MANAGEMENT
  // =============================================================================
  
  const [activeTab, setActiveTab] = useState<string | null>('list');
  const [selectedRoom, setSelectedRoom] = useState<string | null>(roomIdParam);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filters, setFilters] = useState<GetRoomsParams>({
    page: 1,
    limit: 20,
    sortBy: 'name',
    sortOrder: 'asc'
  });

  // =============================================================================
  // URL SYNCHRONIZATION EFFECT
  // =============================================================================
  
  /**
   * Sync URL roomId parameter to component state
   * Enables bookmarkable room detail views
   */
  useEffect(() => {
    const roomId = searchParams.get('roomId');
    if (roomId) {
      setSelectedRoom(roomId);
    } else {
      setSelectedRoom(null);
    }
  }, [searchParams]);

  // Data fetching hooks
  const { 
    data: roomsData, 
    isLoading: roomsLoading, 
    error: roomsError, 
    refetch: refetchRooms 
  } = useRooms(filters);

  const { 
    data: statsData, 
    isLoading: statsLoading, 
    error: statsError, 
    refetch: refetchStats 
  } = useRoomStats({
    ...(filters.buildingId && { buildingId: filters.buildingId }),
    ...(filters.floorId && { floorId: filters.floorId })
  });

  const { data: buildingsData } = useBuildings();

  // Mutation hooks
  const createRoomMutation = useCreateRoom();
  const deleteRoomMutation = useDeleteRoom();

  /**
   * Handle filter changes from child components
   * @param newFilters - Updated filter parameters
   */
  const handleFilterChange = useCallback((newFilters: Partial<GetRoomsParams>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1 // Reset to first page when filters change
    }));
  }, []);

  /**
   * Handle room creation
   * @param data - Room creation data
   */
  const handleCreateRoom = useCallback(async (data: CreateRoomData) => {
    try {
      await createRoomMutation.mutateAsync(data);
      setShowCreateForm(false);
      // Refresh data
      refetchRooms();
      refetchStats();
    } catch (error) {
      console.error('Failed to create room:', error);
    }
  }, [createRoomMutation, refetchRooms, refetchStats]);

  /**
   * Handle room update
   * @param id - Room ID
   * @param data - Room update data
   */
  const handleUpdateRoom = useCallback(async (id: string, data: UpdateRoomData) => {
    // This will be handled by the RoomForm component
    console.log('Update room:', id, data);
  }, []);

  /**
   * Handle room deletion
   * @param id - Room ID
   */
  const handleDeleteRoom = useCallback(async (id: string) => {
    try {
      await deleteRoomMutation.mutateAsync(id);
      // Refresh data
      refetchRooms();
      refetchStats();
    } catch (error) {
      console.error('Failed to delete room:', error);
    }
  }, [deleteRoomMutation, refetchRooms, refetchStats]);

  /**
   * Handle room selection - Opens room detail sidebar
   * Updates URL to enable bookmarking and sharing
   * @param roomId - Selected room ID
   */
  const handleSelectRoom = useCallback((roomId: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('roomId', roomId);
    setSelectedRoom(roomId);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  /**
   * Handle closing room detail sidebar
   * Removes roomId from URL but keeps current tab view
   */
  const handleCloseRoomDetails = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('roomId');
    setSelectedRoom(null);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  /**
   * Refresh all data
   */
  const handleRefresh = useCallback(() => {
    refetchRooms();
    refetchStats();
  }, [refetchRooms, refetchStats]);

  // Loading state
  if (roomsLoading && !roomsData) {
    return (
      <Container size="xl" py="xl">
        <Center h={400}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading room management...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Error state
  if (roomsError) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size="1rem" />}
          title="Error Loading Rooms"
          color="red"
          variant="light"
        >
          Failed to load room data. Please refresh the page and try again.
          <br />
          <Text size="sm" c="dimmed" mt="xs">
            {roomsError.message}
          </Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <div>
            <Title order={1} size="h2" fw={600}>
              Room Management
            </Title>
            <Text c="dimmed" size="sm" mt="xs">
              Manage hospital rooms, capacity, and assignments
            </Text>
          </div>
          
          <Group gap="sm">
            <Button
              variant="outline"
              leftSection={<IconRefresh size="1rem" />}
              onClick={handleRefresh}
              loading={roomsLoading || statsLoading}
            >
              Refresh
            </Button>
            
            <Button
              leftSection={<IconPlus size="1rem" />}
              onClick={() => setShowCreateForm(true)}
              loading={createRoomMutation.isPending}
            >
              Create Room
            </Button>
          </Group>
        </Group>

        {/* Statistics Summary */}
        {statsData && (
          <Paper p="md" radius="md" withBorder>
            <Group justify="space-between" align="center">
              <div>
                <Text size="sm" c="dimmed" mb="xs">
                  Room Overview
                </Text>
                <Group gap="lg">
                  <div>
                    <Text size="lg" fw={600}>
                      {statsData.totalRooms}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Total Rooms
                    </Text>
                  </div>
                  <div>
                    <Text size="lg" fw={600} c="green">
                      {statsData.activeRooms}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Active
                    </Text>
                  </div>
                  <div>
                    <Text size="lg" fw={600} c="red">
                      {statsData.inactiveRooms}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Inactive
                    </Text>
                  </div>
                  <div>
                    <Text size="lg" fw={600}>
                      {statsData.totalCapacity}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Total Capacity
                    </Text>
                  </div>
                </Group>
              </div>
              
              <Badge
                color={statsData.activeRooms > 0 ? 'green' : 'red'}
                variant="light"
                size="lg"
              >
                {statsData.activeRooms > 0 ? 'Operational' : 'No Active Rooms'}
              </Badge>
            </Group>
          </Paper>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab} variant="outline">
          <Tabs.List>
            <Tabs.Tab value="list">
              Room List
            </Tabs.Tab>
            <Tabs.Tab value="stats">
              Statistics
            </Tabs.Tab>
          </Tabs.List>

          {/* Room List Tab */}
          <Tabs.Panel value="list" pt="lg">
            <RoomList
              rooms={roomsData?.data || []}
              {...(roomsData?.pagination && { pagination: roomsData.pagination })}
              {...(roomsLoading !== undefined && { loading: roomsLoading })}
              onFilterChange={handleFilterChange}
              onSelectRoom={handleSelectRoom}
              onUpdateRoom={handleUpdateRoom}
              onDeleteRoom={handleDeleteRoom}
              buildings={buildingsData?.data?.buildings || []}
            />
          </Tabs.Panel>

          {/* Statistics Tab */}
          <Tabs.Panel value="stats" pt="lg">
            <RoomStats
              {...(statsData && { stats: statsData })}
              {...(statsLoading !== undefined && { loading: statsLoading })}
              {...(statsError && { error: statsError })}
              {...(handleRefresh && { onRefresh: handleRefresh })}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* Create Room Modal */}
      {showCreateForm && (
        <RoomForm
          opened={showCreateForm}
          onClose={() => setShowCreateForm(false)}
          onSubmit={handleCreateRoom}
          loading={createRoomMutation.isPending}
          buildings={buildingsData?.data?.buildings || []}
        />
      )}

      {/* Room Detail Sidebar - Opens when room is selected from list */}
      <RoomDetailSidebar
        roomId={selectedRoom}
        opened={!!selectedRoom}
        onClose={handleCloseRoomDetails}
      />
    </Container>
  );
};